import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { applyOverrides, autofix, check, cssVars, derive } from "@/lib/theme/colors";

const CACHE_KEY = "le-theme";
const DRAFT_KEY = "le-theme-draft";
const Ctx = createContext(null);

const readJSON = (store, key) => { try { return JSON.parse(store.getItem(key)); } catch { return null; } };
const writeJSON = (store, key, v) => { try { v == null ? store.removeItem(key) : store.setItem(key, JSON.stringify(v)); } catch { /* ignore */ } };

function writeRoot(vars, density, textScale) {
  const el = document.documentElement;
  Object.entries(vars).forEach(([k, val]) => el.style.setProperty(k, val));
  el.dataset.density = density || "comfortable";
  el.dataset.textSize = textScale || "standard";
}

function clearRoot() {
  const el = document.documentElement;
  Array.from(el.style).filter((k) => k.startsWith("--le-")).forEach((k) => el.style.removeProperty(k));
  delete el.dataset.density;
  delete el.dataset.textSize;
}

// Same order as the server: clinic theme -> personal preset -> personal custom colours.
export function resolveDraft(draft, eff, presets) {
  const byId = Object.fromEntries(presets.map((p) => [p.id, p]));
  let raw;
  if (draft.kind === "clinic") raw = applyOverrides(draft.base, draft.overrides);
  else if (draft.mode === "preset" && byId[draft.preset_id]) raw = byId[draft.preset_id].tokens;
  else if (draft.mode === "custom") raw = applyOverrides(byId[draft.preset_id]?.tokens || eff.clinic.tokens, draft.overrides);
  else raw = eff.clinic.tokens;
  const tokens = autofix(raw);
  return { raw, tokens, vars: derive(tokens), issues: draft.mode === "custom" || draft.kind === "clinic" ? check(raw) : [] };
}

const personalFrom = (eff) => ({
  kind: "personal", mode: eff.mode, preset_id: eff.preset_id || null, overrides: eff.overrides || {},
  density: eff.density, text_scale: eff.text_scale,
});
const sameAsSaved = (d, eff) => JSON.stringify(personalFrom(eff)) === JSON.stringify({ ...personalFrom(eff), ...d });

export function ThemeProvider({ children }) {
  const { user, loading } = useAuth();
  const [eff, setEff] = useState(null);
  const [presets, setPresets] = useState([]);
  const [draft, setDraftState] = useState(() => readJSON(sessionStorage, DRAFT_KEY));
  const [panelOpen, setPanelOpen] = useState(false);

  const setDraft = useCallback((next) => {
    setDraftState((cur) => {
      const v = typeof next === "function" ? next(cur) : next;
      writeJSON(sessionStorage, DRAFT_KEY, v);
      return v;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const [e, p] = await Promise.all([api.get("/appearance/effective"), api.get("/appearance/presets")]);
      setEff(e.data);
      setPresets(p.data.presets || []);
      return e.data;
    } catch { return null; } // keep the safe default theme
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setEff(null);
      setDraft(null);
      writeJSON(localStorage, CACHE_KEY, null);
      clearRoot();
      return;
    }
    load();
  }, [user?.id, loading, load, setDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = useMemo(() => (draft && eff ? resolveDraft(draft, eff, presets) : null), [draft, eff, presets]);
  const personalDraft = draft?.kind === "personal" ? draft : null;
  const dirty = !!draft && (draft.kind === "clinic" || !sameAsSaved(draft, eff || {}));

  useEffect(() => {
    if (!eff) return;
    const vars = cssVars(preview ? preview.vars : eff.vars);
    writeRoot(vars, personalDraft?.density || eff.density, personalDraft?.text_scale || eff.text_scale);
    if (!draft) writeJSON(localStorage, CACHE_KEY, { vars, density: eff.density, text: eff.text_scale });
  }, [eff, draft, preview, personalDraft]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const updateDraft = useCallback((patch) => {
    setDraft((cur) => ({ ...(cur?.kind === "personal" ? cur : personalFrom(eff)), ...patch }));
  }, [eff, setDraft]);

  const cancelPreview = useCallback(() => setDraft(null), [setDraft]);

  const savePersonal = useCallback(async () => {
    const d = draft?.kind === "personal" ? draft : personalFrom(eff);
    const body = { mode: d.mode, preset_id: d.preset_id, overrides: d.mode === "custom" ? d.overrides : null,
      density: d.density, text_scale: d.text_scale, colour_mode: "light" };
    const r = await api.put("/users/me/appearance", body);
    setEff(r.data);
    setDraft(null);
    return r.data;
  }, [draft, eff, setDraft]);

  const resetPersonal = useCallback(async () => {
    const r = await api.delete("/users/me/appearance");
    setEff(r.data);
    setDraft(null);
    return r.data;
  }, [setDraft]);

  const previewClinic = useCallback((form, base, label) => {
    setDraft({ kind: "clinic", base, overrides: form.overrides || {}, form, label });
  }, [setDraft]);

  const value = useMemo(() => ({
    eff, presets, draft, personalDraft, preview, dirty, panelOpen, setPanelOpen,
    updateDraft, cancelPreview, savePersonal, resetPersonal, previewClinic, reload: load,
    settings: personalDraft || (eff ? personalFrom(eff) : null),
  }), [eff, presets, draft, personalDraft, preview, dirty, panelOpen, updateDraft, cancelPreview, savePersonal, resetPersonal, previewClinic, load]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
